import React from 'react'
import { Component } from 'reflux'
import { PurchaseOrderStore, PurchaseOrderStoreActions, STATUS } from '../../stores/purchaseOrderStore'
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Box,
  Divider,
  TextField,
  Backdrop,
  Select,
  MenuItem
} from '@material-ui/core'
import CommentSection from '../../components/commentSection'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Proxies from '../../components/proxies'

const { GET } = Proxies

export default class PurchaseOrderReceiveDetails extends Component {
  constructor(props) {
    super(props)
    this.stores = [PurchaseOrderStore]
    this.storeKeys = ['purchaseOrdersToReceiveDetails', 'status', 'purchaseOrderIDs', 'stockLocations', 'productStockLocations', 'setEANonVariation']
    this.state = {
      receivedAmounts: {},
      expectedForNonWarehouseAmounts: {},
      showStockLocationModal: false,
      // Scanner / selection UI
      scanInput: '',
      lastScan: null,
      scanBusy: false,
      eanCache: {},
      showSelectVariationModal: false,
      selectModal: {
        ean: '',
        query: '',
        page: 0,
        candidateGroups: [],
        infoMessage: ''
      }
    }
  }

  componentDidMount() {
    const purchaseOrderIDs = this.props.location.state?.purchaseOrderIDs || []
    PurchaseOrderStoreActions.fetchMultiplePurchaseOrders(purchaseOrderIDs)
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.purchaseOrdersToReceiveDetails !== prevState.purchaseOrdersToReceiveDetails && !this.state.isSaving) {
      const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
      const grouped = this.groupLinesByVariationAndDescription(allLines)
      const receivedAmounts = {}
      const expectedForNonWarehouseAmounts = {}
      grouped.forEach(group => {
        const groupKey = this.getGroupKey(group[0])
        receivedAmounts[groupKey] = group.reduce((sum, l) => sum + (l.amountReceived || 0), 0)
        expectedForNonWarehouseAmounts[groupKey] = group
          .filter(l => l.orderLineType !== 'WAREHOUSE')
          .reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
      })
      this.setState({ receivedAmounts, expectedForNonWarehouseAmounts })
    }

    // When scan finishes (busy -> idle) and no modal is open, refocus scanner input
    if (prevState.scanBusy && !this.state.scanBusy && !this.state.showSelectVariationModal) {
      this.focusScan()
    }
  }

  groupLinesByVariationAndDescription(lines) {
    const groups = {}
    lines.forEach(line => {
      const key = this.getGroupKey(line)
      if (!groups[key]) groups[key] = []
      groups[key].push(line)
    })
    return Object.values(groups)
  }

  handleReceivedAmountChange = (groupKey, value) => {
    const parsed = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0)
    this.setState(prev => ({
      receivedAmounts: {
        ...prev.receivedAmounts,
        [groupKey]: parsed
      }
    }))
  }

  focusScan = () => {
    if (this.scanRef && this.scanRef.current) {
      try {
        this.scanRef.current.focus()
      } catch (e) {
        console.error('Failed to focus scan input', e)
      }
    }
  }

  scanRef = React.createRef()

  handleScanKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (this.state.scanBusy) return
      const code = (this.state.scanInput || '').trim()
      if (code) this.handleScanSubmit(code)
    }
  }

  resolveVariationIDsForEAN = async ean => {
    const cached = this.state.eanCache[ean]
    if (cached) return cached

    const extractVariationIDs = products => {
      const ids = []
      ;(products || []).forEach(p => {
        ;(p.variations || []).forEach(v => {
          if (String(v.EAN || '') === String(ean)) ids.push(v.VariationID)
        })
      })
      return ids
    }

    let products = await GET('products/pool', {
      filters: [
        { column: 'Variation.EAN', operator: 'EQUAL', values: [ean] },
        { column: 'Product.productStatus', operator: 'EQUAL', values: ['AVAILABLE'] }
      ]
    })
    let ids = extractVariationIDs(products)

    if (!ids.length) {
      products = await GET('products/pool', {
        filters: [{ column: 'Variation.EAN', operator: 'EQUAL', values: [ean] }]
      })
      ids = extractVariationIDs(products)
    }

    if (ids.length) {
      this.setState(prev => ({ eanCache: { ...prev.eanCache, [ean]: ids } }))
    }
    return ids
  }

  handleScanSubmit = async ean => {
    if (this.state.scanBusy) return
    this.setState({ scanBusy: true })
    try {
      const variationIDs = await this.resolveVariationIDsForEAN(ean)

      const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
      const groups = this.groupLinesByVariationAndDescription(allLines)

      const groupsByVar = groups.reduce((acc, g) => {
        const varId = g[0]?.productVariationID
        if (!acc[varId]) acc[varId] = []
        acc[varId].push(g)
        return acc
      }, {})

      const candidateGroups = []
      variationIDs.forEach(vid => {
        const c = groupsByVar[vid] || []
        c.forEach(gr => {
          candidateGroups.push({
            group: gr,
            groupKey: this.getGroupKey(gr[0]),
            description: gr[0]?.description,
            productVariationUID: gr[0]?.productVariationUID,
            VariationID: vid
          })
        })
      })

      if (candidateGroups.length === 1) {
        this.incrementReceived(candidateGroups[0].groupKey, 1)
        this.setState({ lastScan: { ean, result: 'matched', detail: candidateGroups[0] }, scanInput: '', scanBusy: false })
        return
      }

      if (candidateGroups.length > 1) {
        this.setState({
          showSelectVariationModal: true,
          selectModal: { ean, query: '', page: 0, candidateGroups, infoMessage: 'Varen findes i flere linjer. Vælg hvilken.' },
          scanInput: ''
        })
        this.setState({ scanBusy: false })
        return
      }

      const allCandidateGroups = groups.map(gr => ({
        group: gr,
        groupKey: this.getGroupKey(gr[0]),
        description: gr[0]?.description,
        productVariationUID: gr[0]?.productVariationUID,
        VariationID: gr[0]?.productVariationID
      }))

      this.setState({
        showSelectVariationModal: true,
        selectModal: {
          ean,
          query: '',
          page: 0,
          candidateGroups: allCandidateGroups,
          infoMessage: 'Stregkoden matcher ikke noget på denne indkøbsordre. Vælg den korrekte linje på ordren (så gemmer vi EAN på varianten).'
        },
        scanInput: ''
      })
      this.setState({ scanBusy: false })
    } catch (err) {
      this.setState({ lastScan: { ean, result: 'error' }, scanInput: '', scanBusy: false })
    }
  }

  incrementReceived = (groupKey, by = 1) => {
    this.setState(prev => {
      const current = prev.receivedAmounts[groupKey]
      const next = (current === '' || current === null || current === undefined ? 0 : Number(current)) + Number(by || 0)
      return { receivedAmounts: { ...prev.receivedAmounts, [groupKey]: next } }
    })
  }

  handleSelectGroup = async (groupKey, VariationID) => {
    const ean = (this.state.selectModal?.ean || '').trim()
    if (ean && VariationID) {
      try {
        await this.saveEANToVariation(null, VariationID, ean)
      } catch (err) {
        this.setState(prev => ({
          selectModal: { ...prev.selectModal, infoMessage: 'Kunne ikke gemme EAN på varianten. Linjen er valgt, men prøv evt. igen senere.' }
        }))
      }
    }

    this.incrementReceived(groupKey, 1)
    this.setState(
      { showSelectVariationModal: false, selectModal: { ean: '', query: '', page: 0, candidateGroups: [], infoMessage: '' } },
      this.focusScan
    )
  }

  saveEANToVariation = async (ProductID, VariationID, ean) => {
    if (!VariationID || !ean) return
    try {
      PurchaseOrderStoreActions.setEANonVariation(VariationID, ean)
      this.setState(prev => ({ eanCache: { ...prev.eanCache, [ean]: [VariationID] } }))
    } catch (e) {
      console.error('Failed to save EAN to variation', e)
      throw e
    }
  }

  openReceiveHandle = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    this.props.history.push({
      pathname: '/purchase-orders/receive/handle',
      state: { purchaseOrderlines: allLines }
    })
  }

  getGroupKey = line => {
    return `${line.productVariationID}||${line.description}`
  }

  saveRecievedAmount = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const grouped = this.groupLinesByVariationAndDescription(allLines)
    const payload = grouped.map(group => {
      const first = group[0]
      const groupKey = this.getGroupKey(first)
      return {
        receivedAmount: Number(this.state.receivedAmounts[groupKey] ?? 0),
        purchaseOrderLineIDs: group.map(l => l.purchaseOrderLineID)
      }
    })
    PurchaseOrderStoreActions.receiveLines(payload)
  }

  reportError = () => {
    const enrichedLines =
      (this.state.purchaseOrdersToReceiveDetails || []).flatMap(po =>
        (po.purchaseOrderlines || []).map(l => ({
          ...l,
          purchaseOrderNumber: po.purchaseOrderNumber,
          PurchaseOrderID: po.PurchaseOrderID
        }))
      ) || []
    const singleQtyLines = enrichedLines.filter(l => Number(l.amountOfProducts || 0) === 1)

    const grouped = this.groupLinesByVariationAndDescription(singleQtyLines)

    const poData = (this.state.purchaseOrdersToReceiveDetails || []).map(po => ({
      purchaseOrderID: po.PurchaseOrderID,
      purchaseOrderNumber: po.purchaseOrderNumber,
      supplierName: po.supplier?.supplierName || po.supplierName
    }))

    const reportData = grouped
      .map(group => {
        const first = group[0]
        const groupKey = this.getGroupKey(first)

        const expectedAmount = group.length

        const receivedAmount =
          this.state.receivedAmounts[groupKey] !== undefined && this.state.receivedAmounts[groupKey] !== ''
            ? Number(this.state.receivedAmounts[groupKey])
            : group.reduce((sum, l) => sum + Number(l.amountReceived || 0), 0)

        const missingAmount = expectedAmount - receivedAmount

        const missingByPOMap = group
          .filter(l => Number(l.amountReceived || 0) < 1)
          .reduce((acc, l) => {
            const key = `${l.PurchaseOrderID}||${l.purchaseOrderNumber}`
            if (!acc[key]) {
              acc[key] = { PurchaseOrderID: l.PurchaseOrderID, purchaseOrderNumber: l.purchaseOrderNumber, missingCount: 0 }
            }
            acc[key].missingCount += 1
            return acc
          }, {})

        const missingByPurchaseOrders = Object.values(missingByPOMap)

        return {
          productVariationID: first.productVariationID,
          description: first.description,
          expectedAmount,
          receivedAmount,
          missingAmount,
          purchaseOrderLineIDs: group.map(l => l.purchaseOrderLineID),
          missingByPurchaseOrders,
          poData
        }
      })
      .filter(x => x.receivedAmount !== x.expectedAmount)

    PurchaseOrderStoreActions.reportPurchaseOrderError(reportData)
  }

  onSaveClick = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const linesForWarehouse = allLines.filter(
      l =>
        l.orderLineType === 'WAREHOUSE' &&
        this.state.receivedAmounts[this.getGroupKey(l)] - this.state.expectedForNonWarehouseAmounts[this.getGroupKey(l)] > 0
    )
    if (linesForWarehouse.length) {
      PurchaseOrderStoreActions.getStockLocationsForVariations(linesForWarehouse.map(l => l.productVariationID))
      PurchaseOrderStoreActions.getStockLocations()
      this.setState({ showStockLocationModal: true })
    } else {
      this.saveRecievedAmount()
    }
  }

  onSaveStockLocationModalClick = () => {
    this.setState({ showStockLocationModal: false })
    this.saveRecievedAmount()
  }

  render() {
    const { status } = this.state
    const isSaving = status === STATUS.IS_SAVING
    const isLoading = status === STATUS.IS_FETCHING
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const grouped = this.groupLinesByVariationAndDescription(allLines)

    const pageSize = 6
    const modalQuery = (this.state.selectModal?.query || '').trim().toLowerCase()
    const filteredCandidateGroups = (this.state.selectModal?.candidateGroups || []).filter(cg => {
      if (!modalQuery) return true
      const hay = `${cg.productVariationUID || ''} ${cg.description || ''}`.toLowerCase()
      return hay.includes(modalQuery)
    })
    const maxPage = Math.max(0, Math.ceil(filteredCandidateGroups.length / pageSize) - 1)
    const currentPage = Math.min(Math.max(0, this.state.selectModal?.page || 0), maxPage)
    const visibleCandidateGroups = filteredCandidateGroups.slice(currentPage * pageSize, currentPage * pageSize + pageSize)
    const allComplete =
      grouped.length > 0 &&
      grouped.every(group => {
        const expectedAmount = group.reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
        const received = group.reduce((sum, l) => sum + (l.amountReceived || 0), 0)
        return received === expectedAmount && expectedAmount > 0
      })
    const linesForWarehouse = allLines.filter(
      l =>
        l.orderLineType === 'WAREHOUSE' &&
        this.state.receivedAmounts[this.getGroupKey(l)] - this.state.expectedForNonWarehouseAmounts[this.getGroupKey(l)] > 0
    )
    return (
      <React.Fragment>
        <Box p={3} width='100%' maxWidth='100vw' mx='auto' position='relative' onClick={this.focusScan}>
          <Backdrop open={isLoading || isSaving} style={{ zIndex: 1000, color: '#fff' }}>
            <CircularProgress color='inherit' />
          </Backdrop>
          <Typography variant='h5' gutterBottom style={{ fontWeight: 700, marginBottom: 24 }}>
            Modtag indkøbsordre {this.state.purchaseOrdersToReceiveDetails.map(po => po.purchaseOrderNumber).join(', ')}
          </Typography>
          <Divider style={{ marginBottom: 32 }} />
          <Box display='flex' gridGap={24}>
            <Box flex={1} minWidth={0}>
              <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
                Forventet
              </Typography>
              <Paper
                elevation={3}
                style={{
                  marginBottom: 32,
                  padding: 0,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  background: '#fafbfc'
                }}
              >
                <Table size='small' style={{ minWidth: 600, width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 120, fontWeight: 600, background: '#f6f8fa' }}>Varenr.</TableCell>
                      <TableCell style={{ width: 320, fontWeight: 600, background: '#f6f8fa' }}>Beskrivelse</TableCell>
                      <TableCell style={{ width: 80, fontWeight: 600, background: '#f6f8fa', textAlign: 'right' }}>Forventet</TableCell>
                      <TableCell style={{ width: 120, fontWeight: 600, background: '#f6f8fa', textAlign: 'right' }}>Tilføj</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped.map(group => {
                      const first = group[0]
                      const groupKey = this.getGroupKey(first)
                      const expectedAmount = group.reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
                      const recvVal = this.state.receivedAmounts[groupKey]
                      const numericReceived = Number(recvVal === '' || recvVal === null || recvVal === undefined ? 0 : recvVal)
                      const isNotReceived = expectedAmount > 0 && numericReceived === 0
                      const isComplete = expectedAmount > 0 && numericReceived === expectedAmount
                      return (
                        <TableRow key={groupKey} style={{ background: isNotReceived ? '#ffebee' : undefined }}>
                          <TableCell style={{ fontFamily: 'monospace' }}>{first.productVariationUID}</TableCell>
                          <TableCell>
                            <Typography variant='body2' style={{ whiteSpace: 'pre-line', color: isNotReceived ? '#b71c1c' : undefined }}>
                              {first.description}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ textAlign: 'right' }}>{expectedAmount}</TableCell>
                          <TableCell style={{ textAlign: 'right' }}>
                            <Button
                              size='sm'
                              color='primary'
                              onClick={() => {
                                this.incrementReceived(groupKey, 1)
                                this.focusScan()
                              }}
                              disabled={isSaving || isComplete}
                            >
                              +1
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Box>

            <Box flex={1} minWidth={0}>
              <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
                Modtaget (scanner)
              </Typography>
              <Box display='flex' alignItems='center' gridGap={12} mb={2}>
                <TextField
                  inputRef={this.scanRef}
                  autoFocus
                  label='Scan stregkode'
                  variant='outlined'
                  size='small'
                  disabled={this.state.scanBusy}
                  value={this.state.scanInput}
                  onChange={e => this.setState({ scanInput: e?.target?.value ?? '' })}
                  onKeyDown={this.handleScanKeyDown}
                  style={{ width: 320 }}
                />
                <Button
                  onClick={() => this.handleScanSubmit((this.state.scanInput || '').trim())}
                  disabled={!this.state.scanInput || this.state.scanBusy}
                >
                  Tilføj
                </Button>
                {this.state.lastScan && (
                  <Typography variant='body2' color='textSecondary'>
                    Sidste scan: {this.state.lastScan.ean} ({this.state.lastScan.result})
                  </Typography>
                )}
              </Box>

              <Paper elevation={1} style={{ borderRadius: 10, border: '1px solid #e0e0e0', background: '#fafbfc' }}>
                <Table size='small' style={{ minWidth: 600, width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 120, fontWeight: 600, background: '#f6f8fa' }}>Varenr.</TableCell>
                      <TableCell style={{ fontWeight: 600, background: '#f6f8fa' }}>Beskrivelse</TableCell>
                      <TableCell style={{ width: 80, fontWeight: 600, background: '#f6f8fa', textAlign: 'right' }}>Modtaget</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped
                      .filter(group => {
                        const first = group[0]
                        const groupKey = this.getGroupKey(first)
                        return Number(this.state.receivedAmounts[groupKey] || 0) > 0
                      })
                      .map(group => {
                        const first = group[0]
                        const groupKey = this.getGroupKey(first)
                        const expectedAmount = group.reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
                        const value =
                          this.state.receivedAmounts[groupKey] === 0 || this.state.receivedAmounts[groupKey] === null
                            ? ''
                            : this.state.receivedAmounts[groupKey]
                        const numericReceived = Number(value || 0)
                        const isComplete = expectedAmount > 0 && numericReceived === expectedAmount
                        return (
                          <TableRow key={groupKey} style={{ background: isComplete ? '#e8f5e9' : undefined }}>
                            <TableCell style={{ fontFamily: 'monospace' }}>{first.productVariationUID}</TableCell>
                            <TableCell>
                              <Typography variant='body2' style={{ whiteSpace: 'pre-line', color: isComplete ? '#2e7d32' : undefined }}>
                                {first.description}
                              </Typography>
                            </TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                              <TextField
                                variant='outlined'
                                size='small'
                                type='number'
                                inputProps={{ min: 0, style: { textAlign: 'right', width: 80 } }}
                                value={value}
                                onChange={e => this.handleReceivedAmountChange(groupKey, e.target.value)}
                                placeholder='0'
                                disabled={isSaving || isComplete}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Box>

          <Box display='flex' justifyContent='flex-end' mt={4}>
            <button
              style={{
                padding: '10px 32px',
                background: 'red',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16,
                cursor: allComplete ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
                marginRight: 16
              }}
              onClick={this.reportError}
              disabled={isSaving || allComplete}
            >
              Rapportér fejl
            </button>
            <button
              style={{
                padding: '10px 32px',
                background: '#388e3c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16,
                cursor: allComplete ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
                marginRight: 16
              }}
              onClick={this.onSaveClick}
              disabled={isSaving || allComplete}
            >
              Gem
            </button>
            <button
              style={{
                padding: '10px 32px',
                background: '#388e3c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16
              }}
              onClick={this.openReceiveHandle}
              disabled={isSaving}
            >
              Håndter
            </button>
          </Box>

          <Box display={'flex'} flexDirection={'column'} gridGap={24} marginTop={6}>
            {this.state.purchaseOrdersToReceiveDetails.map(po => (
              <Paper key={po.PurchaseOrderID}>
                <Box display={'flex'} flexDirection={'column'} gridGap={2} padding={4}>
                  <Typography variant='h6' gutterBottom style={{ fontWeight: 700 }}>
                    {`Kommentarer for PO Nummer ${po.purchaseOrderNumber}`}
                  </Typography>
                  <CommentSection relatedType='PurchaseOrder' relatedID={po.PurchaseOrderID} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Selection modal for ambiguous or not-found scans */}
        <Modal
          isOpen={this.state.showSelectVariationModal}
          toggle={() => this.setState({ showSelectVariationModal: !this.state.showSelectVariationModal })}
          size='lg'
        >
          <ModalHeader>
            <Typography variant='h6' style={{ fontWeight: 700 }}>
              Vælg produkt/linje for scan {this.state.selectModal.ean}
            </Typography>
          </ModalHeader>
          <ModalBody>
            <Box display='flex' flexDirection='column' gridGap={16}>
              {this.state.selectModal.infoMessage && <Typography variant='body2'>{this.state.selectModal.infoMessage}</Typography>}

              {!!this.state.selectModal.candidateGroups.length && <Box />}

              <Box>
                <Typography variant='subtitle2' style={{ fontWeight: 700, marginBottom: 8 }}>
                  Mulige linjer på ordren
                </Typography>
                <Box display='flex' alignItems='center' justifyContent='space-between' gridGap={12} mb={2}>
                  <TextField
                    variant='outlined'
                    size='small'
                    value={this.state.selectModal.query}
                    onChange={e => {
                      const nextValue = e?.target?.value ?? ''
                      this.setState(prev => ({ selectModal: { ...prev.selectModal, query: nextValue, page: 0 } }))
                    }}
                    placeholder='Søg i ordrelinter…'
                    style={{ width: 420 }}
                  />
                  <Typography variant='body2' color='textSecondary'>
                    {filteredCandidateGroups.length}
                  </Typography>
                </Box>

                {filteredCandidateGroups.length ? (
                  <React.Fragment>
                    {visibleCandidateGroups.map(cg => (
                      <Box
                        key={cg.groupKey}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        padding={1}
                        border='1px solid #efefef'
                        borderRadius={6}
                        mb={1}
                      >
                        <Box>
                          <Typography variant='body2' style={{ fontFamily: 'monospace' }}>
                            {cg.productVariationUID}
                          </Typography>
                          <Typography variant='body2' style={{ whiteSpace: 'pre-line' }}>
                            {cg.description}
                          </Typography>
                        </Box>
                        <Button onClick={() => this.handleSelectGroup(cg.groupKey, cg.VariationID)}>Vælg</Button>
                      </Box>
                    ))}

                    {filteredCandidateGroups.length > pageSize && (
                      <Box display='flex' alignItems='center' justifyContent='space-between' mt={2}>
                        <Button
                          size='sm'
                          disabled={currentPage <= 0}
                          onClick={() =>
                            this.setState(prev => ({
                              selectModal: { ...prev.selectModal, page: Math.max(0, (prev.selectModal.page || 0) - 1) }
                            }))
                          }
                        >
                          Forrige
                        </Button>
                        <Typography variant='body2' color='textSecondary'>
                          Side {currentPage + 1} / {maxPage + 1}
                        </Typography>
                        <Button
                          size='sm'
                          disabled={currentPage >= maxPage}
                          onClick={() =>
                            this.setState(prev => ({
                              selectModal: { ...prev.selectModal, page: Math.min(maxPage, (prev.selectModal.page || 0) + 1) }
                            }))
                          }
                        >
                          Næste
                        </Button>
                      </Box>
                    )}
                  </React.Fragment>
                ) : (
                  <Typography variant='body2' color='textSecondary'>
                    Ingen matchende linjer på ordren for denne scan.
                  </Typography>
                )}
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => this.setState({ showSelectVariationModal: false }, this.focusScan)}>Luk</Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={this.state.showStockLocationModal}
          toggle={() => this.setState({ showStockLocationModal: !this.state.showStockLocationModal })}
          size='lg'
        >
          <ModalHeader>
            <Typography variant='h6' style={{ fontWeight: 700 }}>
              Indtast lagerplacering for produkter bestilt til lager
            </Typography>
          </ModalHeader>
          <ModalBody>
            <Box display={'flex'} flexDirection={'column'} gridGap={12}>
              {this.state.productStockLocations.map((psl, i) => (
                <Box
                  key={psl.ProductID}
                  display={'flex'}
                  alignItems={'center'}
                  gridGap={40}
                  paddingBottom={2}
                  justifyContent={'space-between'}
                  borderBottom={i < this.state.productStockLocations.length - 1 ? '1px solid #efefef' : undefined}
                >
                  <Box display={'flex'} flexDirection={'column'} width={500}>
                    {linesForWarehouse
                      .filter(l => psl.variationIds.includes(l.productVariationID))
                      .reduce((receivedVariationsForWarehouse, curLine) => {
                        const existing = receivedVariationsForWarehouse.find(x => x.description === curLine.description)
                        if (existing) {
                          if (
                            existing.amount <
                            this.state.receivedAmounts[this.getGroupKey(curLine)] -
                              this.state.expectedForNonWarehouseAmounts[this.getGroupKey(curLine)]
                          ) {
                            existing.amount++
                          }
                        } else {
                          receivedVariationsForWarehouse.push({ description: curLine.description, amount: 1 })
                        }
                        return receivedVariationsForWarehouse
                      }, [])
                      .map(l => (
                        <Typography key={l.description}>
                          {l.amount}x {l.description}
                        </Typography>
                      ))}
                  </Box>
                  <Select
                    value={psl.StockLocationID || ''}
                    onChange={v => {
                      PurchaseOrderStoreActions.setProductStockLocation(psl.ProductID, v.target.value)
                    }}
                    style={{ width: 180, height: 40 }}
                    error={!psl.StockLocationID}
                    variant='outlined'
                  >
                    {this.state.stockLocations.map(sl => (
                      <MenuItem key={sl.StockLocationID} value={sl.StockLocationID}>
                        {sl.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ))}
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button onClick={this.onSaveStockLocationModalClick} disabled={this.state.productStockLocations.some(psl => !psl.StockLocationID)}>
              Ok
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    )
  }
}
